<script setup lang="ts">
import { ref, computed } from 'vue';
import type { QuickPublishStatus } from './types';

const props = defineProps<{
  status: QuickPublishStatus;
  canManage: boolean;
}>();

const emit = defineEmits<{ (e: 'quick-publish', publish: boolean): void }>();

const working = ref(false);
const pendingPublish = ref<boolean | null>(null);

const total = computed(() => props.status.total ?? 0);
const published = computed(() => props.status.published ?? 0);
const allPublished = computed(() => total.value > 0 && published.value === total.value);

const lastResult = computed(() => {
  if (props.status.affected === undefined) {
    return null;
  }
  return { affected: props.status.affected, skipped: props.status.skipped ?? 0 };
});

function requestToggle(event: Event) {
  // Revert the visual change until confirmed.
  (event.target as HTMLInputElement).checked = allPublished.value;
  pendingPublish.value = !allPublished.value;
}

function cancel() {
  pendingPublish.value = null;
}

function confirm() {
  if (pendingPublish.value === null) {
    return;
  }
  working.value = true;
  emit('quick-publish', pendingPublish.value);
  setTimeout(() => {
    working.value = false;
    pendingPublish.value = null;
  }, 800);
}
</script>

<template>
  <section class="pp-section pp-quick-publish">
    <div class="pp-section-title">Bulk workflow state publish child</div>

    <p class="small text-muted mb-2">
      Publish or unpublish (workflow state) every node contextualised directly under
      this collection.
    </p>

    <div class="d-flex align-items-center gap-3 flex-wrap">
      <div class="form-check form-switch m-0">
        <input
          id="pp-quick-publish"
          class="form-check-input"
          type="checkbox"
          role="switch"
          :checked="allPublished"
          :disabled="!canManage || working || total === 0"
          @change="requestToggle"
        />
        <label class="form-check-label" for="pp-quick-publish">
          {{ allPublished ? 'All published' : 'Publish all' }}
        </label>
      </div>

      <span class="badge bg-light text-dark border">
        {{ published }} / {{ total }} published
      </span>
    </div>

    <!-- Confirmation -->
    <div v-if="pendingPublish !== null" class="alert alert-warning mt-3 py-2 px-3 small">
      <p class="mb-2">
        {{ pendingPublish ? 'Publish' : 'Unpublish' }} all
        {{ pendingPublish ? status.unpublished : published }} affected node(s) under this collection?
        <br />
        <span class="text-muted">
          Nodes whose workflow transition is not allowed for you will be skipped.
        </span>
      </p>
      <div class="d-flex gap-2">
        <button type="button" class="btn btn-sm btn-warning" :disabled="working" @click="confirm">
          <span v-if="working" class="spinner-border spinner-border-sm me-2" role="status"></span>
          Confirm
        </button>
        <button type="button" class="btn btn-sm btn-outline-secondary" :disabled="working" @click="cancel">
          Cancel
        </button>
      </div>
    </div>

    <!-- Result feedback -->
    <div v-else-if="lastResult" class="small text-muted mt-2">
      <i class="bi bi-check-circle text-success me-1"></i>
      {{ lastResult.affected }} updated<span v-if="lastResult.skipped">, {{ lastResult.skipped }} skipped</span>.
    </div>
  </section>
</template>
